import { Injectable, OnDestroy } from '@angular/core';
import { Headers, Http, Request, RequestMethod, Response } from '@angular/http';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs/Rx';
import { Store } from '@ngrx/store';

import { BaseHttpService } from './base-http.service';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import * as fromRoot from '../store/reducers';
import { AppUiActions } from '../store/actions';
import { HtmlService } from './html.service';
import { ConfigHelper } from './config.helper';

@Injectable()
export class TestContentService extends BaseHttpService implements OnDestroy {

    activeAssessmentItem: PgRendererDto.AssessmentItem;
    activeTestlet: PgRendererDto.TestletDefinition;
    activeTestletId: number;
    testletId$: Observable<number>;
    private assessmentItemSubscription: Subscription;
    private testletSubscription: Subscription;
    private testletIdSubscription: Subscription;

    constructor(
        http: Http,
        httpClient: HttpClient,
        store: Store<fromRoot.State>,
        loggerService: LoggerService,
        authService: AuthService,
        private htmlService: HtmlService
    ) {
        super(http, httpClient, store, loggerService, authService);

        this.testletId$ = this.store.select(fromRoot.getCurrentTestletId);
        this.assessmentItemSubscription = this.store.select(fromRoot.getAssessmentItem).subscribe(assessmentItem => {
            this.activeAssessmentItem = assessmentItem;
        });

        this.testletSubscription = this.store.select(fromRoot.getTestlet).subscribe(testlet => {
            this.activeTestlet = testlet;
        });

        this.testletIdSubscription = this.testletId$.subscribe(testletId => {
            this.activeTestletId = testletId;
        });
    }

    ngOnDestroy() {
        this.assessmentItemSubscription.unsubscribe();
        this.testletSubscription.unsubscribe();
        this.testletIdSubscription.unsubscribe();
    }

    downloadTestContentFile(brandName: string, siteCode: string, stationNumber: number) {
        return this.request({
            method: RequestMethod.Post,
            url: `${this.testContentServerApiUrl}/api/download-test-session-content-file/${brandName}/${siteCode}/${stationNumber}`,
            body: {},
        });
    }

    downloadTestContentFileWithProgress(brandName: string, siteCode: string, stationNumber: number) {
        const url = `${this.testContentServerApiUrl}/api/download-test-session-content-file/${brandName}/${siteCode}/${stationNumber}`;
        return this.requestWithProgress(url, "POST");
    }

    getTestlet(testletId: number, assessmentTestId: string): Observable<PgRendererDto.TestletDefinition> {
        return this.request({
            method: RequestMethod.Get,
            url: `${this.testContentServerApiUrl}/api/testlet/preview/testlet/${testletId}/${assessmentTestId}`
        })
        .flatMap((testlet: PgRendererDto.TestletDefinition) => {
            if (ConfigHelper.isIppMode()) {
                return Observable.of(testlet);
            }

            const html = testlet.AssessmentItems.map(assessmentItem => {
                return assessmentItem.ItemBody.ItemBodyPanels.map(body => body.Html);
            }).join("");

            let resourceIds: number[] = [];

            const regex = /{r(\d+):(video|audio)}/ig;
            let matchedArray: RegExpExecArray;
            while ((matchedArray = regex.exec(html)) != null) {
                resourceIds.push(parseInt(matchedArray[1]));
            }

            resourceIds = Array.from(new Set(resourceIds));

            const loadedResources = this.htmlService.preloadedResources.map( x => x.resourceId);
            //removes things that has been loaded
            resourceIds = resourceIds.filter(
                function (e) {
                    return this.indexOf(e) < 0;
                },
                loadedResources
            );
            if (resourceIds.length > 0) {
                const observableBatch = resourceIds.map(resourceId =>
                    this.requestForBlob({
                        method: RequestMethod.Get,
                        url: this.htmlService.getResourceUrl(resourceId)
                    })
                    .map(responseBlob => {
                        return URL.createObjectURL(responseBlob);
                    })
                );

                return Observable.forkJoin(observableBatch).map(allUrls => {
                    allUrls.forEach((url, index) => {
                        this.htmlService.preloadedResources.push({
                            resourceId: resourceIds[index],
                            objectURL: url
                        });
                    });
                    return testlet;
                });
            } else {
                return Observable.of(testlet);
            }
        });
    }

    getCurrentTestletHasVideoAudio(html: string): boolean {
        const regex = /{r(\d+):(video|audio)}/ig;
        let matchedArray: RegExpExecArray;
        if ((matchedArray = regex.exec(html)) != null) {
            return true;
        } else {
            return false;
        }
    }

    getCurrentTestletHasInteraction(): boolean {
        if (this.activeTestlet && this.activeTestlet.AssessmentItems) {
            if (this.activeTestlet.AssessmentItems.some(x => x.Interactions.length > 0)) {
                return true;
            }else {
                return false;
            }

        }else {
            return false;
        }
    }

    useTestletTimeAdjustment(): boolean {
        const html = this.activeTestlet.AssessmentItems.map(assessmentItem => {
            return assessmentItem.ItemBody.ItemBodyPanels.map(body => body.Html);
        }).join("");
        return !this.getCurrentTestletHasVideoAudio(html) && this.getCurrentTestletHasInteraction();
    }

    getTestlets(testletIds: number[], assessmentTestId: string): Observable<PgRendererDto.TestletDefinition[]> {
        return this.request({
            method: RequestMethod.Post,
            url: `${this.testContentServerApiUrl}/api/testlet/preview/get-testlets-by-ids/${assessmentTestId}`,
            body: testletIds
        });
    }

    getTestletVersion(testletVersionId: number): Observable<PgRendererDto.TestletDefinition> {
        return this.request({
            method: RequestMethod.Get,
            url: `${this.testContentServerApiUrl}/api/testlet/preview/testletversion/${testletVersionId}`
        });
    }

    getSnapshotTestlet(snapshotId: string): Observable<PgRendererDto.TestletSnapshotDto> {
        return this.request({
            method: RequestMethod.Get,
            url: `${this.testContentServerApiUrl}/api/testlet/testlet-snapshot-get?sessionToken=${snapshotId}`
        });
    }

    getAssessmentTestByTestFormId(testFormId: number): Observable<PgRendererDto.AssessmentTest> {
        return this.request({
            method: RequestMethod.Get,
            url: `${this.testContentServerApiUrl}/api/testform/preview/${testFormId}`
        });
    }

    getAssessmentTest(assessmentTestId: number): Observable<PgRendererDto.AssessmentTest> {
        return this.request({
            method: RequestMethod.Get,
            url: `${this.testContentServerApiUrl}/api/assessment-test/${assessmentTestId}`
        });
    }

    getReferencedInteractions(item: PgRendererDto.AssessmentItem): PgRendererDto.InteractionDefinition[] {
        const interactionRefNums: number[] = [];
        const regex = /{i(\d.*?)}/ig;
        let matchedArray: RegExpExecArray;
        const html = item.ItemBody.ItemBodyPanels.map(x => x.Html).join("");

        while ((matchedArray = regex.exec(html)) != null) {
            interactionRefNums.push(parseInt(matchedArray[1]));
        }

        return interactionRefNums.map(x => item.Interactions.find(i => i.InteractionRefNum == x));
    }

    getReferencedInteractionsByTestlet(testlet: PgRendererDto.TestletDefinition): PgRendererDto.InteractionDefinition[] {
        let interactions: PgRendererDto.InteractionDefinition[] = [];
        if (testlet) {
            testlet.AssessmentItems.forEach(i => {
                interactions = interactions.concat(this.getReferencedInteractions(i));
            });
        }
        return interactions;
    }

    getReferencedInteractionsByItemGroup(testlet: PgRendererDto.TestletDefinition, groupNumber: number): PgRendererDto.InteractionDefinition[] {
            let interactions: PgRendererDto.InteractionDefinition[] = [];
            if (testlet) {
                testlet.AssessmentItems.filter(x => x.GroupNumber == groupNumber).forEach(i => {
                    interactions = interactions.concat(this.getReferencedInteractions(i));
                });
            }
            return interactions;
    }

    getPreviewResponseScores(responses: PgRendererDto.InteractionResponse[], snapshotSessionToken: string = null): Observable<PgRendererDto.ResponseScoresDto> {
        return this.request({
            method: RequestMethod.Post,
            url: `${this.testContentServerApiUrl}/api/testlet/response-scores`,
            body: { SnapshotSessionToken: snapshotSessionToken, TestTakerResponses: responses }
        });
    }

    getIppAssessmentTest(ippAssessmentTestId: number): Observable<PgRendererDto.IppAssessmentTestDto> {
        return this.request({
            method: RequestMethod.Get,
            url: `${this.testContentServerApiUrl}/api/IppAssessmentTest/get-ipp-assessment-test-by-id/${ippAssessmentTestId}`
        });
    }

    getIppAssessmentTestByProductId(productId: number): Observable<PgRendererDto.IppAssessmentTestDto> {
        return this.request({
            method: RequestMethod.Get,
            url: `${this.testContentServerApiUrl}/api/IppAssessmentTest/get-ipp-assessment-test-by-product-id/${productId}`
        });
    }

    getProductInfo(ippAssessmentTestId: string): Observable<PgRendererDto.ProductDto> {
        return this.request({
            method: RequestMethod.Get,
            url: `${this.testContentServerApiUrl}/api/IppAssessmentTest/get-product-name-by-ipp-assessment-test-id/${ippAssessmentTestId}`
        });
    }

    sendTestFormValidationResultEmail(testFormId: number, testletIds: number[], errorMessages: string[], recipientEmail: string): Observable<any> {
        return this.request({
            method: RequestMethod.Post,
            url: `${this.testContentServerApiUrl}/api/testform/validate-test-form-email`,
            body: {
                RecipientEmail: recipientEmail,
                TestFormId: testFormId,
                TestletIds: testletIds,
                ErrorMessages: errorMessages
            }
        });
    }

}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/services/test-content.service.ts